import { FormikProps } from "formik";
import { getStepsByForm } from "../constants/calculator-steps";
import {
  CalculatorField,
  CalculatorValues,
  IRenderCalculatorStep,
  LegalForm,
} from "../types";

type FormikCalculator = FormikProps<CalculatorValues>;

const isWorkersCountStep = (step: IRenderCalculatorStep) =>
  step.responsibleFields.includes(CalculatorField.workersCount);

// returns array of visible calculator steps based on form values
export const getVisibleSteps = (formik: FormikCalculator) => {
  const currentLegalForm = formik.values[CalculatorField.legalForm];
  const taxationSystem = formik.values[CalculatorField.taxationSystem];
  const hiredWorkers = formik.values[CalculatorField.hiredWorkers];
  const availableSteps = getStepsByForm(currentLegalForm);

  const filteredSteps = availableSteps.filter((step) => {
    // Крок з кількістю працівників — тільки коли явно обрано "Так"
    if (isWorkersCountStep(step)) {
      return hiredWorkers === true;
    }

    // Filter out single tax group step if "Загальна" is selected for individual entrepreneur
    if (
      currentLegalForm === LegalForm.indEnt &&
      step.id === "3" &&
      taxationSystem === "загальна"
    ) {
      return false;
    }

    // For legal entity with "Загальна" - show only up to taxation system step
    if (
      currentLegalForm === LegalForm.legEnt &&
      taxationSystem === "загальна" &&
      step.id !== "1" &&
      step.id !== "2"
    ) {
      return false;
    }

    return true;
  });

  const lastCompletedStepIndex = findLastStepIndex(formik, filteredSteps);
  const visibleStepsCount = lastCompletedStepIndex + 1;

  return filteredSteps.slice(0, visibleStepsCount);
};

// finds index of last completed step based on filled fields
const findLastStepIndex = (
  formik: FormikCalculator,
  steps: IRenderCalculatorStep[]
) => {
  const lastFilledStepIndex = steps.reduce(
    (completedSteps, { responsibleFields }) => {
      const hasFilledFields = responsibleFields.every(hasValidField(formik));
      return hasFilledFields ? completedSteps + 1 : completedSteps;
    },
    0
  );

  return Math.min(lastFilledStepIndex, steps.length);
};

const hasValidField =
  (formik: FormikCalculator) =>
  (field: CalculatorField): boolean => {
    // workersCount is optional when hiredWorkers is not explicitly true
    if (
      field === CalculatorField.workersCount &&
      formik.values[CalculatorField.hiredWorkers] !== true
    ) {
      return true;
    }

    const fieldValue = formik.values[field];
    const fieldError = formik.errors[field];

    return fieldValue !== null && fieldValue !== undefined && !fieldError;
  };
